import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  useMediaQuery,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
  ChartNoAxesCombined,
  Download,
  Ellipsis,
  Filter,
  FilterX,
  Pencil,
  PencilOff,
  Plus,
  RefreshCw,
  Search,
  Upload,
} from 'lucide-react';
import { type To } from 'react-router-dom';

import {
  Filters,
  type QuickFilterDefinition,
} from '@modules/_shared/components/filters/Filters';
import { RaLink } from '@modules/_shared/components/RaLink';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { parseWhereParam } from '@modules/_shared/utils/listUrlParams';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import { useLocale } from '@shared/hooks/locale';

export const iconButtonSx = {
  color: 'text.secondary',
  transition: 'all 0.15s ease',
  '&:hover': {
    bgcolor: 'grey.100',
    color: 'text.primary',
  },
} as const;

export interface ImportDialogProps {
  open: boolean;
  onImportSuccess: () => void;
  onImporterClose: () => void;
}

export interface ExportDialogProps {
  where: any;
  open: boolean;
  onExporterClose: () => void;
}

export type ListToolbarProps<T = {}> = {
  tableName?: string;
  // The `crm_entity` a saved view (smart list) is filed under, when it must
  // differ from the table (Inbox / Sent share `email_threads`). Must match the
  // `SmartListTitle` above the toolbar, which reads views by the same key.
  smartListEntity?: string;
  quickFilters?: QuickFilterDefinition[];
  viewLinks?: {
    name: string;
    icon: React.ReactNode;
  }[];
  searchComponent?: React.ReactNode;
  leadingActions?: React.ReactNode;
  newLink?: To;
  actions?: React.ReactNode;
  moreActions?: React.ReactNode;
  bulkActions?: React.ReactNode;
  ImportDialog?: React.ElementType<ImportDialogProps & T>;
  ExportDialog?: React.ElementType<ExportDialogProps>;
  refetch?: () => void;
  queryParamsScope?: string;
  defaultFilterTypeExpanded?: 'advanced' | 'quick';
  showRealCountOption?: boolean;
  isScraperSchema?: boolean;
  importProps?: T;
};

export const ListToolbar = ({
  tableName,
  smartListEntity,
  quickFilters,
  viewLinks,
  searchComponent,
  leadingActions,
  newLink,
  actions,
  moreActions,
  bulkActions,
  ImportDialog,
  ExportDialog,
  refetch,
  queryParamsScope,
  defaultFilterTypeExpanded = 'quick',
  showRealCountOption = false,
  isScraperSchema,
  importProps,
}: ListToolbarProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const isAdmin = me?.is_admin;

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );

  const [searchParams, setSearchParams] =
    useScopedSearchParams(queryParamsScope);
  // ?where is user input (bookmarked, hand-edited, stale after a filter
  // rename): a bare JSON.parse threw during render. Each view parses its own
  // copy too, so a page only stops blanking once it uses the same guard.
  const where = parseWhereParam(searchParams.get('where'));
  const isEditing = searchParams.get('edit') === 'true';

  const [showSearch, setShowSearch] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const menuAnchorRef = useRef<HTMLButtonElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const [expandedFilterType, setExpandedFilterType] = useState<
    'advanced' | 'quick' | null
  >(isMobile ? null : defaultFilterTypeExpanded);

  const filterCount = Object.keys(where).length;
  const collapseSearchAfterBlur = useCallback(
    (searchContainer: HTMLElement) => {
      window.setTimeout(() => {
        const activeElement = document.activeElement;
        const autocompletePopper = document.querySelector(
          '.MuiAutocomplete-popper:hover',
        );
        if (
          autocompletePopper != null ||
          (activeElement instanceof HTMLElement &&
            (searchContainer.contains(activeElement) ||
              activeElement.closest('.MuiAutocomplete-popper') != null))
        ) {
          return;
        }

        setShowSearch(false);
      }, 100);
    },
    [],
  );
  useEffect(() => {
    if (!showSearch) {
      return;
    }

    // Focus immediately, then again on the next frame so we win the race
    // against MUI Autocomplete's own internal focus/render pass.
    const focusInput = () => {
      searchContainerRef.current
        ?.querySelector<HTMLInputElement>('input')
        ?.focus();
    };

    focusInput();
    const rafId = window.requestAnimationFrame(focusInput);
    return () => window.cancelAnimationFrame(rafId);
  }, [showSearch]);

  return (
    <>
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          gap: 0.5,
          width: '100%',
          px: 1,
          minHeight: 48,
        }}
      >
        {viewLinks && !(isMobile && showSearch) && !isEditing && (
          <Stack
            direction="row"
            sx={{
              gap: 0.25,
              alignItems: 'center',
            }}
          >
            {viewLinks.map(({ name, icon }) => (
              <IconButton
                key={name}
                size="small"
                sx={iconButtonSx}
                onClick={() => {
                  searchParams.set('view', name);
                  searchParams.delete('limit');
                  searchParams.delete('offset');
                  setSearchParams(searchParams);
                }}
              >
                {icon}
              </IconButton>
            ))}
          </Stack>
        )}

        {leadingActions}

        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 0.5,
            flexGrow: 1,
            justifyContent: 'flex-end',
          }}
        >
          {!isEditing && (
            <ClickAwayListener onClickAway={() => setShowSearch(false)}>
              <Stack
                direction="row"
                sx={{
                  gap: 0.5,
                  flexGrow: 1,
                  justifyContent: 'flex-end',
                }}
              >
                {searchComponent && !showSearch && (
                  <IconButton
                    size="small"
                    sx={iconButtonSx}
                    onClick={() => setShowSearch(!showSearch)}
                  >
                    <Search size={18} />
                  </IconButton>
                )}
                {searchComponent && showSearch && (
                  <Box
                    ref={searchContainerRef}
                    onBlurCapture={event => {
                      collapseSearchAfterBlur(event.currentTarget);
                    }}
                    sx={{
                      display: 'flex',
                      flexGrow: 1,
                      justifyContent: 'flex-end',
                    }}
                  >
                    {searchComponent}
                  </Box>
                )}
              </Stack>
            </ClickAwayListener>
          )}

          {quickFilters && !(isMobile && showSearch) && !isEditing && (
            <IconButton
              size="small"
              sx={iconButtonSx}
              onClick={() =>
                setExpandedFilterType(expandedFilterType ? null : 'quick')
              }
            >
              <Filter size={18} />
              {filterCount > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    minWidth: 14,
                    height: 14,
                    borderRadius: '7px',
                    bgcolor: 'secondary.main',
                    color: 'secondary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.5625rem',
                    fontWeight: 700,
                    px: 0.25,
                  }}
                >
                  {filterCount}
                </Box>
              )}
            </IconButton>
          )}

          {refetch && !(isMobile && showSearch) && !isEditing && (
            <IconButton
              size="small"
              sx={iconButtonSx}
              onClick={() => refetch()}
            >
              <RefreshCw size={18} />
            </IconButton>
          )}

          {!(isMobile && showSearch) && !isEditing && (
            <IconButton
              size="small"
              sx={iconButtonSx}
              onClick={() => setMenuOpen(!menuOpen)}
              ref={menuAnchorRef}
            >
              <Ellipsis size={18} />
            </IconButton>
          )}

          {newLink && !(isMobile && showSearch) && !isEditing && (
            <Button
              variant="contained"
              color="primary"
              disableElevation
              component={RaLink}
              to={newLink}
              preserveSearchParams
              startIcon={<Plus />}
            >
              {t('New')}
            </Button>
          )}

          {isEditing && bulkActions}
          {isEditing && (
            <Button
              onClick={() => {
                searchParams.delete('edit');
                setSearchParams(searchParams);
              }}
              startIcon={<PencilOff />}
            >
              {t('Finish')}
            </Button>
          )}
        </Stack>
        {actions}
      </Stack>
      <Menu
        anchorEl={menuAnchorRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onClick={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 0.5 } } }}
      >
        {tableName && isAdmin && (
          <MenuItem
            onClick={() => {
              setExpandedFilterType(
                expandedFilterType === 'advanced' ? null : 'advanced',
              );
            }}
          >
            <ListItemIcon>
              <Filter size={16} />
            </ListItemIcon>
            {t('Advanced filters')}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            searchParams.delete('where');
            searchParams.delete('smart_list_id');
            setSearchParams(searchParams);
          }}
        >
          <ListItemIcon>
            <FilterX size={16} />
          </ListItemIcon>
          {t('Clear filters')}
        </MenuItem>
        {showRealCountOption && tableName && isAdmin && (
          <MenuItem
            onClick={() => {
              if (searchParams.get('show_real_count') === 'true') {
                searchParams.delete('show_real_count');
              } else {
                searchParams.set('show_real_count', 'true');
              }
              setSearchParams(searchParams);
            }}
          >
            <ListItemIcon>
              <ChartNoAxesCombined size={16} />
            </ListItemIcon>
            {searchParams.get('show_real_count') === 'true'
              ? t('Hide real count')
              : t('Show real count')}
          </MenuItem>
        )}
        {isAdmin && (
          <>
            {ImportDialog || ExportDialog ? <Divider /> : null}
            {ImportDialog && (
              <MenuItem onClick={() => setShowImportDialog(true)}>
                <ListItemIcon>
                  <Upload size={16} />
                </ListItemIcon>
                {t('Import')}
              </MenuItem>
            )}
            {ExportDialog && (
              <MenuItem onClick={() => setShowExportDialog(true)}>
                <ListItemIcon>
                  <Download size={16} />
                </ListItemIcon>
                {t('Export')}
              </MenuItem>
            )}
          </>
        )}
        {bulkActions && [
          <Divider key="bulk-edit-divider" />,
          <MenuItem
            key="bulk-edit-menu-item"
            onClick={() => {
              searchParams.set('edit', 'true');
              setSearchParams(searchParams);
            }}
          >
            <ListItemIcon>
              <Pencil size={16} />
            </ListItemIcon>
            {t('Bulk edit')}
          </MenuItem>,
        ]}
        {refetch && [
          <Divider key="refresh-divider" />,
          <MenuItem
            key="refresh-menu-item"
            onClick={() => {
              refetch();
            }}
          >
            <ListItemIcon>
              <RefreshCw size={16} />
            </ListItemIcon>
            {t('Refresh')}
          </MenuItem>,
        ]}
        {moreActions && [<Divider key="more-actions-divider" />, moreActions]}
      </Menu>
      {isAdmin && ImportDialog && (
        <ImportDialog
          open={showImportDialog}
          onImporterClose={() => setShowImportDialog(false)}
          onImportSuccess={() => {
            refetch?.();
          }}
          {...(importProps ?? {})}
        />
      )}
      {isAdmin && ExportDialog && (
        <ExportDialog
          open={showExportDialog}
          where={where}
          onExporterClose={() => setShowExportDialog(false)}
        />
      )}
      <Filters
        tableName={tableName}
        smartListEntity={smartListEntity}
        isScraperSchema={isScraperSchema}
        expandedFilterType={expandedFilterType}
        quickFilters={quickFilters}
        queryParamsScope={queryParamsScope}
      />
    </>
  );
};
